import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { TextStyle, Color, FontFamily } from "@tiptap/extension-text-style";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import { formatDateTime } from "../../globalFunctions/DateTimeDisplayCleaner";
import { ShareIcon, CheckIcon, EyeIcon } from "@heroicons/react/24/outline";

const safeParseTiptapJsonString = (value) => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (parsed && parsed.type === "doc") return parsed;
    return null;
  } catch {
    return null;
  }
};

const plainTextToTiptapDoc = (plainText) => {
  const text = (plainText || "").trim();
  return {
    type: "doc",
    content: text
      ? [{ type: "paragraph", content: [{ type: "text", text }] }]
      : [],
  };
};

const normalizeArticleContent = (raw) => {
  const parsed = safeParseTiptapJsonString(raw);
  if (parsed) return parsed;
  return plainTextToTiptapDoc(raw || "");
};

const isNodeEmpty = (node) => {
  if (!node) return true;
  if (node.type === "text") return !(node.text || "").trim();
  if (node.type === "hardBreak") return true;
  const content = node.content || [];
  if (!content.length) return true;
  return content.every(isNodeEmpty);
};

const cleanTiptapNode = (node) => {
  if (!node || typeof node !== "object") return node;

  const content = Array.isArray(node.content)
    ? node.content.map(cleanTiptapNode).filter(Boolean)
    : null;

  const cleaned = content ? { ...node, content } : { ...node };

  if (
    cleaned.type === "paragraph" &&
    (cleaned.content || []).every(isNodeEmpty)
  )
    return null;

  return cleaned;
};

const isEmptyParagraph = (node) => {
  if (!node || node.type !== "paragraph") return false;
  const content = node.content || [];
  if (!content.length) return true;

  return content.every((c) => {
    if (!c) return true;
    if (c.type === "text") return !(c.text || "").trim();
    if (c.type === "hardBreak") return true;
    const inner = c.content || [];
    return !inner.length;
  });
};

const cleanTiptapDoc = (doc) => {
  if (!doc || doc.type !== "doc") return doc;

  const cleaned = [];
  let emptyStreak = 0;

  (doc.content || []).forEach((node) => {
    if (isEmptyParagraph(node)) {
      emptyStreak += 1;
      if (emptyStreak <= 1) cleaned.push(node);
      return;
    }

    emptyStreak = 0;
    cleaned.push(node);
  });

  return { ...doc, content: cleaned };
};

function ArticleTiptapViewer({ valueJson }) {
  const content = useMemo(() => {
    const normalized = normalizeArticleContent(valueJson);
    return cleanTiptapDoc(normalized);
  }, [valueJson]);

  const editor = useEditor({
    editable: false,
    content,
    editorProps: {
      attributes: {
        class: "tiptap-article w-full outline-none",
      },
    },
    extensions: [
      StarterKit.configure({
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Underline,
      TextStyle,
      Color,
      FontFamily.configure({ types: ["textStyle"] }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Link.configure({
        openOnClick: true,
        autolink: true,
        linkOnPaste: true,
        validate: (href) => isSafeHttpUrl(href),
        HTMLAttributes: {
          rel: "noopener noreferrer nofollow",
          target: "_blank",
        },
      }),
    ],
  });

  if (!editor) return null;

  return (
    <div className="w-full">
      <style>{`
        .tiptap-article {
          font-size: 16px;
          line-height: 24px;
          color: #111827;
          font-family: "Open Sans", sans-serif;
          padding: 0;
        }

        .tiptap-article > * { margin: 0; }
        .tiptap-article > * + * { margin-top: 8px; }

        .tiptap-article p { margin: 0; white-space: pre-wrap; }

        .tiptap-article a {
          text-decoration: underline;
          font-weight: 600;
        }

        .tiptap-article ul,
        .tiptap-article ol {
          padding-left: 1.25rem;
          list-style-position: outside;
          margin: 0;
        }

        .tiptap-article ol { list-style-type: decimal; }
        .tiptap-article ul { list-style-type: disc; }

        .tiptap-article li {
          display: list-item;
          margin: 2px 0;
        }

        .tiptap-article li > p { margin: 0; }

        .tiptap-article ul[data-type="taskList"] {
          list-style: none;
          padding-left: 0;
          margin: 0;
        }

        .tiptap-article li[data-type="taskItem"] {
          list-style: none;
          display: flex;
          align-items: flex-start;
          gap: 8px;
          margin: 2px 0;
        }

        .tiptap-article li[data-type="taskItem"] > label {
          display: flex;
          align-items: center;
          margin-top: 2px;
          flex: 0 0 auto;
        }

        .tiptap-article li[data-type="taskItem"] > div {
          flex: 1 1 auto;
          min-width: 0;
        }

        .tiptap-article li[data-type="taskItem"] > div > p { margin: 0; }
      `}</style>
      <EditorContent editor={editor} />
    </div>
  );
}

export default function NewsArticleContent({ article }) {
  if (!article) return null;

  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    if (!article.slug) window.location.reload();
    const url = `https://ananassolutions.com/actualites/${article.slug}`;

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto text-primary px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35 }}
        className="w-full h-[260px] sm:h-[320px] md:h-[380px]"
      >
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </motion.div>

      <div className="mt-6 flex flex-wrap justify-between items-center gap-3">
        <div className="flex items-center gap-4">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.45 }}
            className="text-sm text-gray-600 border-l-4 border-yellow-400 pl-3 py-1"
          >
            Publié le {formatDateTime(article.publishDate, true)} par{" "}
            <a
              className="font-semibold underline"
              href={`/compagnie/${article.companySlug}`}
            >
              {article.companyName || "Compagnie"}
            </a>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.45 }}
            className="flex items-center gap-1 text-gray-600 text-sm"
          >
            <EyeIcon className="w-4 h-4" />
            <span>{article.viewCount || 0}</span>
          </motion.div>
        </div>

        <button
          onClick={handleShare}
          className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition 
            ${
              copied
                ? "border-green-500 text-green-600 bg-green-50 shadow-sm"
                : "border-gray-300 text-gray-700 hover:bg-gray-100"
            }`}
        >
          {copied ? (
            <CheckIcon className="w-4 h-4" />
          ) : (
            <ShareIcon className="w-4 h-4" />
          )}
          {copied ? "Copié !" : "Partager"}
        </button>
      </div>

      <motion.h1
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.45 }}
        className="mt-6 text-4xl font-bold leading-tight tracking-tight text-[#222]"
      >
        {article.title}
      </motion.h1>

      <div className="mt-6">
        <ArticleTiptapViewer valueJson={article.text || ""} />
      </div>
    </div>
  );
}
